/**
 * Position dots under the Recently viewed swipe row in the search panel.
 *
 * The dots are rendered by Liquid (predictive-search-products-list.liquid),
 * one per card, so they survive predictive-search.js morphing the panel.
 * This element only marks the active dot while the row scrolls and scrolls
 * the row when a dot is tapped. The row is looked up on every use (it is the
 * element just before this one) because a morph may swap it out.
 */
class SearchRecentDots extends HTMLElement {
  #frame = 0;

  connectedCallback() {
    // Scroll events don't bubble, so listen in the capture phase.
    document.addEventListener('scroll', this.#onScroll, { capture: true, passive: true });
    window.addEventListener('resize', this.#onResize, { passive: true });
    this.addEventListener('click', this.#onClick);
    this.#schedule();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#frame);
    document.removeEventListener('scroll', this.#onScroll, { capture: true });
    window.removeEventListener('resize', this.#onResize);
    this.removeEventListener('click', this.#onClick);
  }

  get #list() {
    const list = this.previousElementSibling;
    return list instanceof HTMLElement && list.classList.contains('search-recent__list') ? list : null;
  }

  /** @param {HTMLElement} list */
  #cards(list) {
    return /** @type {HTMLElement[]} */ (Array.from(list.children).filter((el) => el instanceof HTMLElement));
  }

  get #dots() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.search-recent__dot')));
  }

  /** @param {Event} event */
  #onScroll = (event) => {
    if (event.target === this.#list) this.#schedule();
  };

  #onResize = () => this.#schedule();

  #schedule() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  }

  #update() {
    const list = this.#list;
    if (!list) return;

    const cards = this.#cards(list);
    const dots = this.#dots;
    if (cards.length === 0 || dots.length === 0) return;

    const maxScroll = list.scrollWidth - list.clientWidth;
    let active = 0;

    if (maxScroll > 0 && list.scrollLeft >= maxScroll - 2) {
      // Wide panels show more than one card, so the last card may never reach
      // the start edge; the last dot wins once the row hits its end.
      active = cards.length - 1;
    } else {
      const start = cards[0].offsetLeft;
      let closest = Infinity;
      cards.forEach((card, index) => {
        const distance = Math.abs(card.offsetLeft - start - list.scrollLeft);
        if (distance < closest) {
          closest = distance;
          active = index;
        }
      });
    }

    dots.forEach((dot, index) => {
      if (index === active) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const dot = event.target instanceof Element ? event.target.closest('.search-recent__dot') : null;
    const list = this.#list;
    if (!(dot instanceof HTMLElement) || !list) return;

    const cards = this.#cards(list);
    const card = cards[this.#dots.indexOf(dot)];
    if (!card) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    list.scrollTo({
      left: card.offsetLeft - cards[0].offsetLeft,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  };
}

if (!customElements.get('search-recent-dots')) {
  customElements.define('search-recent-dots', SearchRecentDots);
}
